import { clientLogger } from '@/lib/clientLogger';
import { useState, useEffect, useRef } from 'react';
import { useBackNavigationLayer } from '@/lib/navigation';
import { useParams } from 'react-router-dom';
import { FileText, Download, PenTool, CheckCircle, AlertCircle, Loader, DollarSign } from 'lucide-react';
import { ENV } from '@/config/environment';
import { SignaturePad } from './SignaturePad';
import { DepositDueNotice, PublicPaymentModal } from './PublicPaymentModal';
import { PaymentConfirmationBanner } from './PaymentConfirmationBanner';
import { usePaymentConfirmationPolling } from '@/hooks/usePaymentConfirmationPolling';
import { openDownloadUrl } from '@/lib/capacitor';

const API_BASE_URL = (ENV.API.BASE_URL || '').replace(/\/$/, '');
const PUBLIC_API_BASE_URL = API_BASE_URL.replace(/\/api$/i, '');

interface PublicEstimateData {
  estimateId: string;
  estimateNumber: string;
  date: string;
  expirationDate?: string;
  viewed?: boolean;
  signaturePath?: string;
  /** Including tax. */
  total?: number;
  amountPaid?: number;
  depositAmount?: number | null;
  /** Converted estimates are paid through the job's invoice, never here. */
  isConverted?: boolean;
  canAcceptPayments: boolean;
  connectedAccountId?: string;
}

const toEstimateData = (raw: any): PublicEstimateData => ({
  estimateId: raw.estimate_id,
  estimateNumber: raw.estimate_number,
  date: raw.date,
  expirationDate: raw.expiration_date,
  viewed: raw.viewed,
  signaturePath: raw.signature_path || '',
  total: raw.total,
  amountPaid: raw.amount_paid,
  depositAmount: raw.deposit_amount,
  isConverted: raw.is_converted,
  canAcceptPayments: raw.can_accept_payments ?? false,
  connectedAccountId: raw.connected_account_id,
});

type PageState = 'loading' | 'error' | 'estimate' | 'signing' | 'completed';

export function PublicEstimatePage() {
  const { estimateId, token } = useParams<{
    estimateId: string;
    token: string;
  }>();

  const [state, setState] = useState<PageState>('loading');
  const [estimateData, setEstimateData] = useState<PublicEstimateData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [signatureSuccessMessage, setSignatureSuccessMessage] = useState('');
  const [pdfRefreshKey, setPdfRefreshKey] = useState(0);
  const [isPdfLoading, setIsPdfLoading] = useState(true);
  const hasMarkedViewed = useRef(false);
  const hasFetched = useRef(false);

  // After a payment, poll until the webhook has recorded it, then refresh the numbers and the PDF.
  const { banner: paymentBanner, startPolling } = usePaymentConfirmationPolling(
    `${PUBLIC_API_BASE_URL}/public/estimates/${estimateId}/${token}`,
    'estimate',
    (raw) => {
      setEstimateData(toEstimateData(raw));
      setIsPdfLoading(true);
      setPdfRefreshKey((k) => k + 1);
    },
  );

  useEffect(() => {
    if (!estimateId || !token) {
      setState('error');
      setErrorMessage('Invalid URL parameters');
      return;
    }

    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchEstimateData();
  }, [estimateId, token]);

  const fetchEstimateData = async () => {
    try {
      setState('loading');

      const response = await fetch(`${PUBLIC_API_BASE_URL}/public/estimates/${estimateId}/${token}`);

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new Error('This estimate link is invalid or has expired.');
        } else if (response.status === 404) {
          throw new Error('Estimate not found.');
        } else {
          throw new Error('Failed to load estimate. Please try again later.');
        }
      }

      // Normalize API response (snake_case) to UI shape (camelCase)
      const raw = await response.json();
      const data = toEstimateData(raw);

      setEstimateData(data);
      setState('estimate');

      if (!data.viewed) {
        await markAsViewed();
      }

    } catch (error) {
      clientLogger.error('Error fetching estimate:', error);
      setState('error');
      setErrorMessage(error instanceof Error ? error.message : 'An unexpected error occurred');
    }
  };

  const markAsViewed = async () => {
    if (hasMarkedViewed.current) {
      return;
    }

    hasMarkedViewed.current = true;

    try {
      await fetch(`${PUBLIC_API_BASE_URL}/public/estimates/${estimateId}/${token}/viewed`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      clientLogger.warn('Failed to mark estimate as viewed:', error);
      hasMarkedViewed.current = false;
    }
  };

  const getFileEndpointUrl = () => `${PUBLIC_API_BASE_URL}/public/estimates/${estimateId}/${token}/file`;

  const handleDownload = () => {
    if (!estimateData) return;
    const downloadUrl = getFileEndpointUrl();
    openDownloadUrl(downloadUrl);
  };

  const handleSignEstimate = () => {
    setShowSignaturePad(true);
  };

  const handleSignatureComplete = async (signaturePath: string, signerName: string) => {
    try {
      setState('signing');

      const response = await fetch(`${PUBLIC_API_BASE_URL}/public/estimates/${estimateId}/${token}/signature`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ signature_path: signaturePath, signed_by_name: signerName }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit signature');
      }

      setState('estimate');
      setSignatureSuccessMessage('Thank you! Your signature has been submitted successfully.');
      setShowSignaturePad(false);
      setEstimateData((prev) => (prev ? { ...prev, signaturePath } : prev));
      setIsPdfLoading(true); // Show loading while PDF regenerates with signature
      setPdfRefreshKey((k) => k + 1);
    } catch (error) {
      clientLogger.error('Error submitting signature:', error);
      setState('error');
      setErrorMessage('Failed to submit signature. Please try again.');
    }
  };

  const closeSignaturePad = useBackNavigationLayer({
    enabled: showSignaturePad,
    onBack: () => setShowSignaturePad(false)
  });

  const handleSignatureCancel = () => {
    closeSignaturePad();
  };

  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader className="mx-auto h-8 w-8 animate-spin text-blue-600 mb-4" />
          <p className="text-gray-600">Loading estimate...</p>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Unable to Load Estimate</h1>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (state === 'completed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <CheckCircle className="mx-auto h-12 w-12 text-green-500 mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Estimate Signed Successfully</h1>
          <p className="text-gray-600 mb-6">
            Thank you for signing this estimate. A confirmation has been sent to the issuer.
          </p>
        </div>
      </div>
    );
  }

  if (!estimateData) return null;

  const remainingBalance = Math.round(((estimateData.total ?? 0) - (estimateData.amountPaid ?? 0)) * 100) / 100;
  const canPay = estimateData.canAcceptPayments && !estimateData.isConverted && remainingBalance > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {signatureSuccessMessage && state === 'estimate' && (
          <div className="mb-4 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              <span>{signatureSuccessMessage}</span>
            </div>
          </div>
        )}

        <PaymentConfirmationBanner banner={paymentBanner} />

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <FileText className="h-7 w-7 sm:h-8 sm:w-8 text-blue-600" />
              <div>
                <h1 className="text-base sm:text-lg font-semibold text-gray-900">Estimate {estimateData.estimateNumber}</h1>
                <p className="text-xs sm:text-sm text-gray-500">{estimateData.date}</p>
              </div>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              {canPay && (
                <button
                  onClick={() => setShowPaymentModal(true)}
                  className="flex-1 sm:flex-initial px-3 sm:px-4 py-2.5 text-sm font-semibold text-white bg-purple-600 border border-purple-700 rounded-lg shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2 transition-colors flex items-center justify-center gap-2"
                >
                  <DollarSign className="h-4 w-4" />
                  <span>Pay</span>
                </button>
              )}
              <button
                onClick={handleDownload}
                className="flex-1 sm:flex-initial px-3 sm:px-4 py-2.5 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center justify-center gap-2"
              >
                <Download className="h-4 w-4" />
                <span>Download</span>
              </button>
              <button
                onClick={handleSignEstimate}
                disabled={showSignaturePad}
                className="flex-1 sm:flex-initial px-3 sm:px-4 py-2.5 text-sm font-medium text-white bg-green-600 border border-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <PenTool className="h-4 w-4" />
                <span>Sign</span>
              </button>
            </div>
          </div>
          {canPay && (
            <DepositDueNotice
              total={estimateData.total ?? 0}
              amountPaid={estimateData.amountPaid ?? 0}
              depositAmount={estimateData.depositAmount}
            />
          )}
        </div>

        {/* PDF Viewer */}
        <div className="bg-white rounded-lg shadow-sm border p-3 sm:p-6 mb-4 sm:mb-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Estimate Document</h2>
          <div className="w-full relative">
            {isPdfLoading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 rounded z-10">
                <Loader className="h-8 w-8 animate-spin text-blue-600 mb-2" />
                <span className="text-sm text-gray-600">Generating PDF...</span>
              </div>
            )}
            <iframe
              src={`${getFileEndpointUrl()}?r=${pdfRefreshKey}#navpanes=0&toolbar=0&view=FitH`}
              className="w-full h-[60vh] sm:h-[80vh] border border-gray-200 rounded"
              title="Estimate PDF"
              onLoad={() => setIsPdfLoading(false)}
              allowFullScreen
            />
          </div>
        </div>

        {/* Payment Modal */}
        {estimateData.connectedAccountId && (
          <PublicPaymentModal
            isOpen={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            createIntentUrl={`${PUBLIC_API_BASE_URL}/public/estimates/${estimateId}/${token}/create-payment-intent`}
            connectedAccountId={estimateData.connectedAccountId}
            totalAmount={estimateData.total ?? 0}
            amountPaid={estimateData.amountPaid ?? 0}
            depositAmount={estimateData.depositAmount}
            documentLabel="Estimate"
            onSuccess={(paidAmount: number) => {
              setShowPaymentModal(false);
              const paidBefore = estimateData.amountPaid ?? 0;
              // Optimistic update: immediately reflect payment in UI
              setEstimateData((prev) =>
                prev ? { ...prev, amountPaid: Math.round((paidBefore + paidAmount) * 100) / 100 } : prev
              );
              startPolling(paidBefore);
            }}
          />
        )}

        {/* Signature Modal */}
        {showSignaturePad && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50">
            <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full">
              <SignaturePad
                onSignatureComplete={handleSignatureComplete}
                onCancel={handleSignatureCancel}
                initialSignaturePath={estimateData.signaturePath}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
